import { Paper, Title, Toolbar, useLogin } from "ui";
import { Languages, useAICaptionStore } from "./ai-caption-store";
import {
  Autocomplete,
  Button,
  Divider,
  FormControl,
  Grid2,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Mic } from "@mui/icons-material";
import { EditList, RemovedLists, ReplaceLists } from "./ReplaceLists";

export function AICaptions() {
  const { t } = useTranslation();
  const error = useAICaptionStore((state) => state.error);

  return (
    <>
      <Paper item xs={12} md={12} lg={6}>
        <Toolbar>
          <Title>{t("AI Captions")}</Title>
        </Toolbar>
        {error && <Grid2>{error}</Grid2>}
        <Grid2 container spacing={1} padding={1} justifyContent="center">
          <AICaptionsContent />
        </Grid2>
      </Paper>
      <ReplaceLists />
      <EditList />
      <RemovedLists />
    </>
  );
}

function AICaptionsContent() {
  const { t } = useTranslation();
  const credentials = useAICaptionStore((state) => state.credentials);

  const getCredentials = useAICaptionStore((state) => state.getCredentials);

  const user = useLogin((state) => state.user);

  if (!user?.roles.includes("ai-captions")) {
    return (
      <Grid2>{t("AI Captions have not been activated for your account")}</Grid2>
    );
  }

  if (!credentials) {
    return (
      <Button
        onClick={async () => {
          await getCredentials();
        }}
      >
        {t("Get credentials")}
      </Button>
    );
  }

  return (
    <>
      <CaptureAudio />
      <ControlAICaptions />
    </>
  );
}

function CaptureAudio() {
  const { t } = useTranslation();
  const startAudioCapture = useAICaptionStore(
    (state) => state.startAudioCapture
  );
  const stopAudioCapture = useAICaptionStore((state) => state.stopAudioCapture);
  const soundActive = useAICaptionStore((state) => state.soundActive);
  const audiosource = useAICaptionStore((state) => state.audiosource);

  if (soundActive) {
    return (
      <>
        <Grid2
          size={6}
          container
          justifyContent="space-evenly"
          alignItems="center"
          spacing={2}
        >
          <Mic htmlColor="green" />{" "}
          {audiosource === "ScreenCapture"
            ? t("Computer audio")
            : t("Microphone")}
        </Grid2>
        <Grid2 size={6} container justifyContent="center" alignItems="center">
          <Button onClick={stopAudioCapture}>Stop audio capture</Button>
        </Grid2>
      </>
    );
  }

  return (
    <>
      <Grid2
        size={6}
        container
        justifyContent="space-evenly"
        alignItems="center"
        spacing={2}
      >
        <Mic htmlColor="red" />
      </Grid2>
      <Grid2 size={6}>
        <FormControl fullWidth>
          <InputLabel>Audio source</InputLabel>
          <Select
            onChange={(e) => {
              useAICaptionStore.setState({
                audiosource: e.target.value as string,
              });
            }}
            label="Audio source"
            value={audiosource}
          >
            <MenuItem value="ScreenCapture">{t("Computer audio")}</MenuItem>
            <MenuItem value="Microphone">{t("Microphone")}</MenuItem>
          </Select>
        </FormControl>
      </Grid2>
      <Grid2 size={12} container justifyContent="center" alignItems="center">
        <Button onClick={startAudioCapture}>Capture audio</Button>
      </Grid2>
      <Grid2 size={12} padding={2}>
        <Divider />
      </Grid2>
      <Grid2
        container
        direction="column"
        size={12}
        spacing={1}
        padding={1}
        alignContent="center"
      >
        <Typography variant="body2">
          {t(
            "If you want to capture computer audio, you can either select the relevant tab from your browser and make sure to enable 'Share tab audio' in the dialog that opens."
          )}
        </Typography>
        <Typography variant="body2">
          {t(
            "If you are using an external program like zoom or teams, you need to to select 'Entire screen' and enable 'Share system audio' in the dialog."
          )}
        </Typography>
        <Typography variant="body2">
          {t(
            "You can also choose microphone as audio source to listen to the microphone connected to this device."
          )}
        </Typography>
      </Grid2>
    </>
  );
}

function ControlAICaptions() {
  const { t } = useTranslation();
  const languages = useAICaptionStore((state) => state.languages);
  const soundActive = useAICaptionStore((state) => state.soundActive);
  const active = useAICaptionStore((state) => state.active);

  const stopStreaming = useAICaptionStore((state) => state.stopStreaming);
  const startStreaming = useAICaptionStore((state) => state.startStreaming);

  if (!soundActive) return null;

  if (active) {
    return (
      <Grid2 size={12} container justifyContent="center" alignItems="center">
        <Button onClick={stopStreaming}>Stop AI Captioning</Button>
      </Grid2>
    );
  }

  return (
    <>
      <Grid2 size={12}>
        <Autocomplete
          options={Languages}
          getOptionLabel={(option) => option.name}
          multiple
          renderInput={(props) => <TextField {...props} label="Language" />}
          value={languages}
          onChange={(e, value) => {
            useAICaptionStore.setState({
              languages: value,
            });
          }}
        />
      </Grid2>
      <Grid2 size={12} container justifyContent="center" alignItems="center">
        <Button onClick={startStreaming}>Start AI Captioning</Button>
      </Grid2>
      <Grid2 size={12} padding={2}>
        <Divider />
      </Grid2>
      <Grid2
        container
        direction="column"
        size={12}
        spacing={1}
        padding={1}
        alignContent="center"
      >
        <Typography variant="body2">
          {t(
            "You can bind a button to toggle this on/off quickly under Key bindings."
          )}
        </Typography>
        <Typography variant="body2">
          {t(
            "For best results, use only 1 language if possible. If you use multiple languages, select the main one first."
          )}
        </Typography>
        <Typography variant="body2">
          {t(
            "If you choose Swedish as the only language, it will still understand and transcribe english as well."
          )}
        </Typography>
        <Typography variant="body2">
          {t(
            "If you instead choose English as the only language, it will not transcribe any Swedish unless you add it as the second language."
          )}
        </Typography>
        <Typography variant="body2">
          {t(
            "In order to update language setting, you need to stop and start the transcription again."
          )}
        </Typography>
      </Grid2>
    </>
  );
}
